// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useRef, useState } from 'react';
import { Button } from '@loa/ui-components';
import type { ProfileFormVersion } from '../../api/profileFormApi';

export interface VersionHistoryControlProps {
  /** Newest first, as the versions API returns. */
  versions: ProfileFormVersion[];
  /** The currently displayed version's versionNo. */
  selectedVersionNo: number;
  onSelect: (version: ProfileFormVersion) => void;
}

function formatDate(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

/**
 * "Version History" picker for the per-client assessment pages (Advisor 2.0
 * Figma nodes 1540-19871 / 1543-21725): an outlined button opening a list of
 * every submitted version; selecting one renders it through the page's
 * readout. Assessments are living, versioned records — history is read-only.
 */
export function VersionHistoryControl({
  versions,
  selectedVersionNo,
  onSelect,
}: Readonly<VersionHistoryControlProps>) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [open]);

  if (versions.length === 0) return null;

  return (
    <div ref={containerRef} className="relative" data-testid="version-history-control">
      <Button
        variant="outlined"
        colour="purple"
        size="md"
        onClick={() => setOpen((v) => !v)}
        data-testid="version-history-button"
      >
        Version History
      </Button>
      {open && (
        <ul
          data-testid="version-history-list"
          className="absolute right-0 top-full z-10 mt-xs m-0 list-none rounded-lg border border-purple-primary bg-white p-xs shadow-card"
        >
          {versions.map((v) => (
            <li key={v.id}>
              <button
                type="button"
                data-testid={`version-history-item-${v.versionNo}`}
                onClick={() => {
                  onSelect(v);
                  setOpen(false);
                }}
                className={`flex w-full items-center justify-between gap-md rounded-lg border-0 bg-transparent px-sm py-sm text-left font-sans text-body-03 cursor-pointer hover:bg-page-bg ${
                  v.versionNo === selectedVersionNo ? 'font-medium text-purple-primary' : 'text-black'
                }`}
              >
                <span>Version {v.versionNo}</span>
                <span className="text-grey-01">{formatDate(v.submittedAt)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default VersionHistoryControl;
